import { useCallback } from "react";
import { Doc } from "sharedb/lib/client";

import { FieldDataType, WorksheetType } from "../shared/types/worksheet";
import { BLOCKS_PATH, DATA_PATH } from "../shared/worksheets/constants";
import { hasEditableBlocks } from "../shared/worksheets/doc-helpers";

import { ReleaseStatus, isArchived } from "./activity";
import { rollbarAndLogError } from "./logger";
import { presentSession, usePresentation } from "./presentation";
import { useQueryParamsManager } from "./routing/hooks/use-query-params-manager";
import type { ModelType } from "./type-utils";
import { shouldAddOverlayParam } from "./urls";
import { blankInstructorNotesWorksheetData } from "./worksheet";

import { serverCopyPage, serverMovePageWithinSection } from "api/api-server-actions";
import {
  ACCESS_ID_QUERY_PARAM,
  FOLLOWING_QUERY_PARAM,
  SECONDARY_OVERLAY_QUERY_PARAM,
  SUBMISSION_ID_QUERY_PARAM,
  SecondaryOverlayType,
} from "components/constants";
import {
  ActivityPageType,
  ActivityType,
  InteractionMode,
  PageCategory,
  PageType,
  SectionPageCategory,
  coursePageCategories,
} from "components/server-types";
import { toastReleasePage, toastUnreleasePage } from "helpers/toasts";
import { t } from "i18n/i18n";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { EXAMPLE_CONTENT_BLOCKS, EXAMPLE_CONTENT_DATA } from "seeds/worksheets/example-blocks";
import { useCapabilities, usePageCapabilitiesForPageId } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  ActivityWithPagesType,
  selectCurrentSectionId,
  selectCurrentUserId,
  selectIsPracticeClassSession,
  selectScreensharingClassUserId,
  selectSelectedPageTabVariant,
} from "store/selectors";
import {
  toastCopyOperationCatcher,
  toastLocalizedOperationCatcher,
  toastMoveOperationCatcher,
  toastSuccessMessage,
} from "utils/alerts";
import { isSharedClassPage, isStaticPage } from "utils/page-utils";
import { useNavigate } from "utils/routing";

export const PAGE_LIST_CUTOFF_LENGTH = 10;

export const pageTitle = (pageCategory: PageCategory) => {
  return t(`common.default.${pageCategory}`);
};

export const isAssignment = (pageCategory: PageCategory) => {
  return pageCategory === "assignment";
};

export const isSectionPageCategory = (pageCategory: PageCategory) => {
  return coursePageCategories.includes(pageCategory as SectionPageCategory);
};

export const isSectionPage = (page: PageType) => {
  return isSectionPageCategory(page.category);
};

export const sumActivityTimes = (activityList: ActivityType[]) => {
  return activityList.reduce((total, activity) => total + Number(activity.estimated_time), 0);
};

export const getReleaseStatus = (pages: PageType[]): ReleaseStatus =>
  pages.length === 0
    ? null
    : pages.every((page) => page.released_at)
      ? "released"
      : pages.some((page) => page.released_at)
        ? "partiallyReleased"
        : "unreleased";

// Releases a set of pages for a topic or activity.
// TODO: Eventually we'd like a way to make bulk updates via storeApi
export const changeReleaseStatusForPages = async (pages: PageType[]) => {
  let pagesToUpdate = pages.filter((page) => !page.released_at);
  const shouldReleasePages = pagesToUpdate.length > 0;
  if (!shouldReleasePages) {
    pagesToUpdate = pages;
  }

  const length = pagesToUpdate.length;

  try {
    await Promise.all(
      pagesToUpdate.map((page) =>
        storeApi.pages.partial_update({
          id: page.id,
          released_at: shouldReleasePages ? new Date().toJSON() : null,
        }),
      ),
    );

    if (shouldReleasePages) {
      toastReleasePage(length);
    } else {
      toastUnreleasePage(length);
    }
  } catch (e) {
    toastLocalizedOperationCatcher("update_pages")(e);
  }
};

/*
 * The following hook returns functions for maintaining pages in the store.
 */
export const usePageActions = () => {
  const { canAuthorSection } = useCapabilities();
  const sectionId = useAppSelector(selectCurrentSectionId);
  const userId = useAppSelector(selectCurrentUserId);
  const screensharingClassUserId = useAppSelector(selectScreensharingClassUserId);
  const isSmallScreen = !useIsMdOrLarger();

  const navigate = useNavigate();
  const { setQueryParams, getQueryParam } = useQueryParamsManager();

  const { presentedSessionId, isPresenting, isFollowing, presentedPageId } = usePresentation();
  const isPracticeClassSession = useAppSelector(selectIsPracticeClassSession);

  const savePage = useCallback(
    (page: Partial<PageType> & ModelType) => storeApi.pages.partial_update(page),
    [],
  );

  const dropPage = useCallback(
    (droppedPage: ActivityPageType, activities: ActivityWithPagesType[]) => {
      if (!activities) {
        savePage({ id: droppedPage.id, order: droppedPage.order });
        return;
      }

      const targetActivity = activities.find((a) => a.id === droppedPage.activity_id);
      if (!targetActivity) {
        // We expect to find an activity, but it wasn't passed to us, this is probably an actual error
        const activityIds = activities.map((a) => a.id);
        rollbarAndLogError(`Dragging page failed due to missing target activity`, {
          pageId: droppedPage.id,
          activityIds,
        });
      }

      // Dragging a page to the archive should unrelease it
      if (isArchived(targetActivity.category) && droppedPage.released_at) {
        const confirmedDropToArchive = window.confirm(
          "Are you sure you want to unpublish this page? It will no longer be visible to students.",
        );

        if (confirmedDropToArchive) {
          // This also unpublishes the page
          savePage({
            id: droppedPage.id,
            released_at: null,
            activity_id: droppedPage.activity_id,
            order: droppedPage.order,
          });
        } else {
          // This forces a re-fresh, getting the server-side data and re-rendering the sidebar
          savePage({ id: droppedPage.id });
        }
      } else {
        savePage({ id: droppedPage.id, activity_id: targetActivity.id, order: droppedPage.order });
      }
    },
    [savePage],
  );

  const selectPage = useCallback(
    (page: PageType | null, pageUrl?: URL) => {
      // 1. We need to update the session to present the new page
      if (isPresenting) {
        if (page?.id !== presentedPageId) {
          presentSession(presentedSessionId, {
            page,
            userId,
            isPractice: isPracticeClassSession,
            accessId: null,
            isRollup: false,
          });
        }
      }

      // 2. We need to update the url params to reflect the new page

      // Set the isFollowing parameter based on our class session state
      // This shouldn't happen, but an instructor could type it into a url.
      const instructorIsFollowing = isPresenting && isFollowing;
      // If the user is following but is an instructor, we can delete the
      // follow param when they select a page that isn't being presented.
      const instructorNavigatedAway =
        isFollowing && canAuthorSection && page?.id !== presentedPageId;

      if (pageUrl) {
        const urlParams = pageUrl.searchParams;
        urlParams.delete(ACCESS_ID_QUERY_PARAM);
        urlParams.delete(SUBMISSION_ID_QUERY_PARAM);

        if (urlParams.get(SECONDARY_OVERLAY_QUERY_PARAM) === "outcomes") {
          urlParams.delete(SECONDARY_OVERLAY_QUERY_PARAM);
        }

        if (!screensharingClassUserId && (instructorIsFollowing || instructorNavigatedAway)) {
          urlParams.delete(FOLLOWING_QUERY_PARAM);
        } else if (isFollowing && !pageUrl.searchParams.get(FOLLOWING_QUERY_PARAM)) {
          // If the user is student and is following, we want to ensure that
          // the follow param is carried over when they view a different page.
          urlParams.set(FOLLOWING_QUERY_PARAM, "1");
        }
      } else {
        const paramsToUpdate = { [ACCESS_ID_QUERY_PARAM]: null, [SUBMISSION_ID_QUERY_PARAM]: null };

        if (getQueryParam(SECONDARY_OVERLAY_QUERY_PARAM) === "outcomes") {
          paramsToUpdate[SECONDARY_OVERLAY_QUERY_PARAM] = null;
        }

        if (!screensharingClassUserId && (instructorIsFollowing || instructorNavigatedAway)) {
          paramsToUpdate[FOLLOWING_QUERY_PARAM] = null;
        }

        setQueryParams(paramsToUpdate);
      }
    },
    [
      isPresenting,
      isFollowing,
      canAuthorSection,
      presentedPageId,
      presentedSessionId,
      userId,
      isPracticeClassSession,
      screensharingClassUserId,
      getQueryParam,
      setQueryParams,
    ],
  );

  // Normally when we call select page we are just updating the existing url, but it's possible
  // we are switching pages entirely, so we need to set any parameters it would set on our new url
  // not on the existing search pages.
  const navigateToPage = (page: PageType, pageUrl: string) => {
    const newUrl = new URL(pageUrl, window.location.origin);

    if (!newUrl.searchParams.has(SECONDARY_OVERLAY_QUERY_PARAM)) {
      const overlay = getQueryParam(SECONDARY_OVERLAY_QUERY_PARAM) as SecondaryOverlayType;
      if (shouldAddOverlayParam(overlay, isSmallScreen)) {
        newUrl.searchParams.set(SECONDARY_OVERLAY_QUERY_PARAM, overlay);
      }
    }

    selectPage(page, newUrl);
    navigate(newUrl.pathname + newUrl.search);
  };

  const createPage = useCallback(
    async (category: PageCategory, activity_id?: string) => {
      const isSeedingNewWorksheets = getQueryParam("seedWorksheets") === "1";
      const initial_worksheet_data = isSeedingNewWorksheets
        ? ({
            [BLOCKS_PATH]: EXAMPLE_CONTENT_BLOCKS,
            [DATA_PATH]: EXAMPLE_CONTENT_DATA as unknown as FieldDataType, // TODO: Make example data match FieldDataType
          } as Partial<WorksheetType>)
        : {};

      const page = await storeApi.pages.create({
        category,
        title: pageTitle(category),
        section_id: sectionId!,
        activity_id,
        initial_worksheet_data,
        interaction_mode: InteractionMode.STATIC,
        instructor_notes_worksheet_data: blankInstructorNotesWorksheetData(),
      });

      return page;
    },
    [getQueryParam, sectionId],
  );

  const deletePage = useCallback((page: PageType) => storeApi.pages.destroy(page.id), []);

  const lockPage = useCallback(
    (page: PageType) => {
      savePage({ id: page.id, locked_at: new Date().toISOString() });
    },
    [savePage],
  );

  const unlockPage = useCallback(
    (page: PageType) => {
      savePage({ id: page.id, locked_at: null });
    },
    [savePage],
  );

  const copyPage = useCallback(
    (
      category: PageCategory,
      itemId: string,
      { sectionIds, activityIds }: { sectionIds?: string[]; activityIds?: string[] },
    ) => {
      serverCopyPage(itemId, sectionIds, activityIds)
        .then(() => {
          const i18nCategory = category;
          toastSuccessMessage(
            t("content_view.item_copied", { item: t(`glossary.${i18nCategory}`) }),
          );
        })
        .catch(toastCopyOperationCatcher(category));
    },
    [],
  );

  const movePageWithinSection = useCallback(
    (
      itemId: string,
      {
        destinationCategory,
        destinationTopicId,
      }: { destinationCategory: PageCategory; destinationTopicId?: string },
    ) => {
      // When a page is moved to the "activity_page" category, from the user's perspective it's moved
      // to another Topic. Better handling later would be to also show the Topic's name in the toast.
      const destinationKey =
        destinationCategory === "activity_page" ? "topic" : destinationCategory;

      serverMovePageWithinSection(itemId, destinationCategory, destinationTopicId)
        .then(() => {
          toastSuccessMessage(
            t("content_view.item_moved", {
              destinationCategory: t(`glossary.${destinationKey}`),
            }),
          );
        })
        .catch(toastMoveOperationCatcher(destinationKey));
    },
    [],
  );

  return {
    createPage,
    savePage,
    deletePage,
    lockPage,
    unlockPage,
    copyPage,
    dropPage,
    selectPage,
    navigateToPage,
    movePageWithinSection,
  };
};

export const useShouldShowStudentSubmitButton = (
  page: PageType | undefined,
  isDocLoaded: boolean,
) => {
  const selectedPageTabVariant = useAppSelector(selectSelectedPageTabVariant);
  const { canMakeResponse } = usePageCapabilitiesForPageId(page?.id);

  const isWorkspaceOrSubmissionPage = ["my_draft", "my_submission"].includes(
    selectedPageTabVariant,
  );
  const pageIsStatic = page && isStaticPage(page);
  const isSharedPage = isSharedClassPage(page);
  const isPlannerEligible = !pageIsStatic || page?.due_at;

  return (
    isPlannerEligible &&
    !isSharedPage &&
    canMakeResponse &&
    isWorkspaceOrSubmissionPage &&
    isDocLoaded
  );
};

// If a static page gets a response block added, the system always makes it an "Individual" interaction mode
export const updatePageStaticContentStatus = (page: PageType, doc: Doc<WorksheetType>) => {
  const isStaticContent = !hasEditableBlocks(doc);
  const pageIsStatic = isStaticPage(page);
  if (isStaticContent && !pageIsStatic) {
    storeApi.pages.partial_update({ id: page.id, interaction_mode: InteractionMode.STATIC });
  } else if (!isStaticContent && pageIsStatic) {
    storeApi.pages.partial_update({ id: page.id, interaction_mode: InteractionMode.INDIVIDUAL });
  }
};

export const markPageAssessmentsStatus = async (pageId: string, complete: boolean) => {
  await storeApi.pages.partial_update({
    id: pageId,
    assessing_marked_complete_at: complete ? new Date().toISOString() : null,
  });
};
